/**
 * ProfileDetailPage — `/admin/business-profiles/:subVerticalKey`
 *
 * 4-tab detail view for a single BusinessProfile:
 *   1. Overview       — identity, engine, delivery mode (editable)
 *   2. Modules        — active + excluded module IDs (editable tag list)
 *   3. Scheduling     — slot durations, buffer, overbooking policy (editable)
 *   4. Deployments    — deploy history (read-only audit trail)
 *
 * Deploy button in the header opens the DeployDialog (SideSheet).
 */
import { ArrowLeft, CaretRight, Rocket } from '@phosphor-icons/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Button } from '../../shared/ui/Button';
import { StatusBadge, type StatusVariant } from '../../shared/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../shared/ui/Tabs';
import { cn } from '../../shared/utils/cn';
import { useBusinessProfile } from '../../features/business-profiles/useBusinessProfiles';
import type { ProfileStatus } from '../../features/business-profiles/types';
import { DeployDialog } from './DeployDialog';
import { DeploymentsTab } from './tabs/DeploymentsTab';
import { ModulesTab } from './tabs/ModulesTab';
import { OverviewTab } from './tabs/OverviewTab';
import { SchedulingTab } from './tabs/SchedulingTab';

type TabId = 'overview' | 'modules' | 'scheduling' | 'deployments';

const VALID_TABS = new Set<TabId>(['overview', 'modules', 'scheduling', 'deployments']);

function isValidTab(value: string | undefined): value is TabId {
  return typeof value === 'string' && VALID_TABS.has(value as TabId);
}

const STATUS_VARIANT: Record<ProfileStatus, StatusVariant> = {
  draft: 'neutral',
  shadow: 'info',
  active: 'success',
  deprecated: 'warning',
  archived: 'neutral',
};

export function ProfileDetailPage(): JSX.Element {
  const { t } = useTranslation('adminProfiles');
  const params = useParams<{ subVerticalKey: string; tab?: string }>();
  const profileQuery = useBusinessProfile(params.subVerticalKey);
  const [deployOpen, setDeployOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  if (!params.subVerticalKey) {
    return <Navigate to="/admin/business-profiles" replace />;
  }

  if (profileQuery.isLoading) {
    return <LoadingSkeleton />;
  }

  if (profileQuery.isError || !profileQuery.data) {
    return (
      <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
        <div
          role="alert"
          className="px-biz-content-lg py-biz-content-lg rounded-biz-card bg-biz-danger-subtle text-biz-danger border border-biz-danger/30"
        >
          {t('detail.notFound')}
        </div>
      </div>
    );
  }

  const profile = profileQuery.data;

  const handleChange = (next: string) => {
    if (isValidTab(next)) {
      setActiveTab(next);
    }
  };

  return (
    <div className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto">
      <nav className="mb-biz-content-lg text-biz-sm" aria-label={t('detail.breadcrumbLabel')}>
        <ol className="flex items-center gap-biz-content text-biz-muted-foreground">
          <li>
            <Link
              to="/admin/business-profiles"
              className="inline-flex items-center gap-1 hover:text-biz-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-biz-ring rounded"
            >
              <ArrowLeft size={16} weight="regular" aria-hidden="true" />
              {t('detail.backToList')}
            </Link>
          </li>
          <li aria-hidden="true">
            <CaretRight size={14} weight="regular" />
          </li>
          <li className="text-biz-foreground font-medium truncate">{profile.displayName}</li>
        </ol>
      </nav>

      <header className="flex items-start justify-between gap-biz-content-lg flex-col md:flex-row mb-biz-section">
        <div className="min-w-0">
          <div className="flex items-center gap-biz-content-lg flex-wrap mb-biz-content">
            <h1 className="text-biz-3xl biz-heading font-semibold">{profile.displayName}</h1>
            <StatusBadge
              variant={STATUS_VARIANT[profile.status]}
              label={t(`status.${profile.status}` as const)}
            />
          </div>
          <div className="flex items-center gap-biz-content-lg flex-wrap text-biz-sm text-biz-muted-foreground">
            <span className="font-mono">{profile.subVerticalKey}</span>
            <span aria-hidden="true">·</span>
            <span className="font-mono">v{profile.profileVersion}</span>
            <span aria-hidden="true">·</span>
            <span>
              {t(`engines.${profile.engine}` as const, { defaultValue: profile.engine })}
            </span>
          </div>
        </div>

        <Button
          intent="primary"
          size="lg"
          leftIcon={<Rocket size={20} weight="regular" />}
          onClick={() => setDeployOpen(true)}
        >
          {t('detail.deployCta')}
        </Button>
      </header>

      <Tabs
        value={activeTab}
        onValueChange={handleChange}
        className="space-y-biz-section"
      >
        <TabsList
          orientation="horizontal"
          aria-label={t('detail.tabsLabel')}
          className={cn('overflow-x-auto -mx-biz-card-lg px-biz-card-lg flex-nowrap')}
        >
          <TabsTrigger value="overview">{t('tabs.overview')}</TabsTrigger>
          <TabsTrigger value="modules">{t('tabs.modules')}</TabsTrigger>
          <TabsTrigger value="scheduling">{t('tabs.scheduling')}</TabsTrigger>
          <TabsTrigger value="deployments">{t('tabs.deployments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab record={profile} />
        </TabsContent>
        <TabsContent value="modules">
          <ModulesTab record={profile} />
        </TabsContent>
        <TabsContent value="scheduling">
          <SchedulingTab record={profile} />
        </TabsContent>
        <TabsContent value="deployments">
          <DeploymentsTab subVerticalKey={profile.subVerticalKey} />
        </TabsContent>
      </Tabs>

      <DeployDialog open={deployOpen} onOpenChange={setDeployOpen} profile={profile} />
    </div>
  );
}

function LoadingSkeleton(): JSX.Element {
  return (
    <div
      className="px-biz-card-lg py-biz-section max-w-[1200px] mx-auto"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="h-8 w-1/3 bg-biz-muted rounded animate-pulse mb-biz-content" />
      <div className="h-4 w-1/5 bg-biz-muted rounded animate-pulse mb-biz-section" />
      <div className="h-40 bg-biz-muted rounded-biz-card animate-pulse" />
    </div>
  );
}
